/**
 * Système de logging centralisé pour UIC Activity Booklet
 * Supprime automatiquement les logs en production pour éviter les logs publics
 */

interface LogContext {
  component?: string;
  action?: string;
  data?: unknown;
  userId?: string;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isClient = typeof window !== 'undefined';

  /**
   * Log de debug - uniquement en développement
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`🔧 [DEBUG] ${message}`, context ? this.formatContext(context) : '');
    }
  }

  /**
   * Log d'information - uniquement en développement
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`ℹ️ [INFO] ${message}`, context ? this.formatContext(context) : '');
    }
  }

  /**
   * Warning - visible en développement, silencieux en production sauf erreurs critiques
   */
  warn(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.warn(`⚠️ [WARN] ${message}`, context ? this.formatContext(context) : '');
    }
    // En production, log uniquement côté serveur pour monitoring
    else if (!this.isClient) {
      console.warn(`[WARN] ${message}`, context);
    }
  }

  /**
   * Erreur - toujours loggée mais formatée différemment selon l'environnement
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (this.isDevelopment) {
      console.error(`❌ [ERROR] ${message}`, error, context ? this.formatContext(context) : '');
    } else {
      // En production, log côté serveur uniquement avec moins de détails
      if (!this.isClient) {
        console.error(`[ERROR] ${message}`, {
          error: error instanceof Error ? error.message : String(error),
          context: context ? this.sanitizeContext(context) : undefined,
        });
      }
    }
  }

  /**
   * Log spécialisé pour les hooks React Query
   */
  queryHook(hookName: string, action: 'start' | 'success' | 'error', data?: unknown): void {
    if (this.isDevelopment) {
      const emoji = action === 'start' ? '🔄' : action === 'success' ? '✅' : '❌';
      console.log(`${emoji} [${hookName}] ${action}:`, data);
    }
  }

  /**
   * Log pour les performances (uniquement en dev)
   */
  performance(operation: string, duration: number, context?: LogContext): void {
    if (this.isDevelopment) {
      const color = duration > 1000 ? '🐌' : duration > 500 ? '⚡' : '🚀';
      console.log(`${color} [PERF] ${operation}: ${duration}ms`, context ? this.formatContext(context) : '');
    }
  }

  /**
   * Log pour les invalidations de cache
   */
  cache(action: string, target: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`🗂️ [CACHE] ${action} - ${target}`, context ? this.formatContext(context) : '');
    }
  }

  /**
   * Log pour les erreurs de navigation/routing
   */
  navigation(message: string, path?: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`🧭 [NAV] ${message}${path ? ` - ${path}` : ''}`, context ? this.formatContext(context) : '');
    }
  }

  /**
   * Log pour les erreurs d'images
   */
  image(message: string, url?: string, error?: unknown): void {
    if (this.isDevelopment) {
      console.log(`🖼️ [IMAGE] ${message}${url ? ` - ${url}` : ''}`, error || '');
    }
  }

  /**
   * Formate le contexte pour l'affichage
   */
  private formatContext(context: LogContext): string {
    const parts = [];
    if (context.component) parts.push(`Component: ${context.component}`);
    if (context.action) parts.push(`Action: ${context.action}`);
    if (context.userId) parts.push(`User: ${context.userId}`);
    return parts.length > 0 ? `[${parts.join(', ')}]` : '';
  }

  /**
   * Sanitise le contexte pour la production (supprime les données sensibles)
   */
  private sanitizeContext(context: LogContext): Partial<LogContext> {
    return {
      component: context.component,
      action: context.action,
      // Retire les données utilisateur et les informations sensibles
    };
  }

  /**
   * Méthode pour supprimer complètement les logs en production
   */
  suppressInProduction<T extends (...args: unknown[]) => unknown>(fn: T): T {
    if (this.isDevelopment) {
      return fn;
    }
    return (() => {}) as T;
  }
}

// Instance singleton
export const logger = new Logger();

